import { Modal } from 'components/ModalV2';
import { useEffect } from 'preact/hooks';
import LoadingScreen from 'components/LoadingScreen';
import { useDetectDevice } from 'hooks';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button';
import { useGlobalConfigContext } from 'GlobalConfig.context';
import { useCancel } from './hooks';
import {
  BodyContainer,
  ConfirmMessage,
  Container,
  CTAButtonsContainer,
  ErrorMessage,
} from './CancelRequestModal.styles';
import { ReturnShipment } from '../../models/ReturnShipment.model';
import { useCurrentShipment } from '../../../../hooks/useCurrentShipment';

interface CancelRequestModalProps {
  onCloseModal: () => void;
  isModalOpen: boolean;
  shipment: ReturnShipment;
}

export const CancelRequestModal = ({
  isModalOpen,
  shipment,
  onCloseModal,
}: CancelRequestModalProps): JSX.Element => {
  const { t } = useTranslation();
  const { isMobile } = useDetectDevice();

  const { organizationId } = useGlobalConfigContext();
  const { reloadShipment } = useCurrentShipment();

  const { process, onCancel } = useCancel({
    orgId: organizationId,
    returnUuid: shipment.returnTicket.returnUuid,
    returnStatus: shipment.returnTicket.returnStatus,
  });

  useEffect(() => {
    if (process.success) {
      reloadShipment();
      onCloseModal();
    }
  }, [process.success]);

  const onCancelReturn = async () => {
    await onCancel();
  };

  const renderBody = () => {
    if (process.loading) {
      return <LoadingScreen />;
    }

    return (
      <BodyContainer isFullWidth justify="space-between" direction="column">
        <ConfirmMessage data-testid="cancel-request-confirm-msg">
          {t('RETURN_CANCEL_RETURN_CONFIRMATION')}
        </ConfirmMessage>
        {process.error && (
          <ErrorMessage data-testid="cancel-request-error-msg">
            {process.error.message}
          </ErrorMessage>
        )}
        <CTAButtonsContainer
          isFullWidth
          direction={isMobile ? 'column' : 'row'}
          justify={isMobile ? 'center' : 'flex-end'}
          gap={8}
        >
          <Button block={isMobile} onClick={onCloseModal} variant="outlined">
            {t('BACK_CTA')}
          </Button>
          <Button block={isMobile} onClick={onCancelReturn} variant="fulfilled">
            {t('CONFIRM_CTA')}
          </Button>
        </CTAButtonsContainer>
      </BodyContainer>
    );
  };

  return (
    <Modal
      closable={false}
      onClose={onCloseModal}
      isOpen={isModalOpen}
      aria={{
        labelledby: 'cancel-request-modal-heading',
      }}
    >
      <Modal.Header id="cancel-request-modal-heading">
        {t('RETURN_CANCEL_RETURN_REQUEST')}
      </Modal.Header>
      <Modal.Body>
        <Container
          isMobile={isMobile}
          align="center"
          justify={process.loading ? 'center' : 'space-between'}
          direction="column"
        >
          {renderBody()}
        </Container>
      </Modal.Body>
    </Modal>
  );
};
